/** Stable agreement-lifetime sibling: selection never tears down money queries. */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useAuth } from "@bw/shared/hooks";
import { sameCompanyBillingScope } from "@bw/shared/utils";
import type { CompanyReceiptOriginal, CompanySoftwareReceiptApi, ModuleAgreementReceiptsProps } from "./companyReceiptContracts";
import { companyBillingDenial } from "./useModuleBilling";
import { useCompanyReceipt, useCompanyReceipts, sameReceiptOriginal, receiptOriginal } from "./useModuleReceipts";
import { ModuleReceiptHistory } from "./ModuleReceiptHistory";
import { ModuleReceiptDetail } from "./ModuleReceiptDetail";

const isReceiptOperation = (value: string): value is keyof CompanySoftwareReceiptApi => ["listReceipts", "getReceipt"].includes(value);

export function ModuleAgreementReceipts({ copy, selection, billingFenceRevision, onReceiptDenied, onReceiptRevalidated }: ModuleAgreementReceiptsProps) {
  const { user } = useAuth();
  const scope = useMemo(() => ({ actorId: user?.uid ?? "", orgId: copy.org_id, acceptanceId: copy.acceptance_id,
    environment: copy.offer.environment }), [user?.uid, copy.org_id, copy.acceptance_id, copy.offer.environment]);
  const scopeId = JSON.stringify(scope), selectionId = JSON.stringify(selection), purchaseId = selection?.purchase_id ?? null;
  const live = useRef({ active: true, scope, selectionId, billingFenceRevision });
  live.current = { ...live.current, scope, selectionId, billingFenceRevision };
  const current = () => live.current.active && sameCompanyBillingScope(scope, live.current.scope)
    && live.current.selectionId === selectionId && live.current.billingFenceRevision === billingFenceRevision;
  const [acknowledgedFence, setAcknowledgedFence] = useState(0), [denied, setDenied] = useState(false);
  const deniedRef = useRef(false), seen = useRef(new Set<Error>()), recheckOwner = useRef<symbol | null>(null);
  const [rechecking, setRechecking] = useState(false);
  const [paging, setPaging] = useState({ selectionId, offset: 0 });
  const [detail, setDetail] = useState<{ selectionId: string; original: CompanyReceiptOriginal } | null>(null);
  const original = detail?.selectionId === selectionId ? detail.original : null;
  const offset = paging.selectionId === selectionId ? paging.offset : 0;
  const suspendAutomaticReads = denied || billingFenceRevision !== acknowledgedFence;
  const receipts = useCompanyReceipts(scope, billingFenceRevision, suspendAutomaticReads, purchaseId, { limit: 10, offset });
  const receipt = useCompanyReceipt(scope, billingFenceRevision, suspendAutomaticReads, purchaseId, original);
  const errors = [receipts.error, receipt.error];

  useLayoutEffect(() => { live.current.active = true; return () => { live.current.active = false; }; }, [scopeId]);
  useEffect(() => {
    if (billingFenceRevision !== acknowledgedFence) {
      deniedRef.current = true; setDenied(true); setDetail(null);
    }
  }, [billingFenceRevision, acknowledgedFence]);
  useEffect(() => {
    for (const error of errors) {
      if (!error || seen.current.has(error) || !current() || purchaseId === null) continue;
      seen.current.add(error);
      const event = companyBillingDenial(error);
      if (!event || !isReceiptOperation(event.operation) || !sameCompanyBillingScope(event.scope, scope)) continue;
      deniedRef.current = true; setDenied(true); setDetail(null);
      onReceiptDenied({ ...event, operation: event.operation, purchaseId });
    }
  });

  async function recheckAccess() {
    if (!current() || purchaseId === null || recheckOwner.current !== null || receipts.isFetching || receipt.isFetching) return;
    const owner = Symbol(); recheckOwner.current = owner; setRechecking(true);
    try {
      const result = await receipts.refetch();
      if (!current() || !result.isSuccess || result.data.purchase_id !== purchaseId) return;
      deniedRef.current = false; setDenied(false); setAcknowledgedFence(billingFenceRevision);
      onReceiptRevalidated({ scope, billingFenceRevision, operation: "listReceipts", purchaseId });
    } finally {
      if (recheckOwner.current === owner) {
        recheckOwner.current = null;
        if (live.current.active && sameCompanyBillingScope(scope, live.current.scope)) setRechecking(false);
      }
    }
  }

  // Hooks remain mounted even with null records/detail. Scope cleanup removes
  // agreement queries only when the actual actor/org/env/agreement is torn down.
  if (selection === null || selection.acceptance_id !== scope.acceptanceId) return null;
  const immediateLoss = errors.some(error => {
    const event = companyBillingDenial(error);
    return error && !seen.current.has(error) && event && isReceiptOperation(event.operation)
      && sameCompanyBillingScope(event.scope, scope);
  });
  const busy = rechecking || receipts.isFetching || receipt.isFetching;
  if (denied || immediateLoss || billingFenceRevision !== acknowledgedFence) return <Alert severity="warning">
    Company payment record access is unavailable.
    <Button size="small" disabled={busy} onClick={() => { void recheckAccess(); }}>Recheck receipt access</Button>
  </Alert>;
  return <Stack spacing={1.5} aria-label="Company purchase records">
    <Typography>Payment records for purchase created {new Date(selection.created_at).toLocaleString()}</Typography>
    {receipts.isPending ? <Typography role="status">Loading recorded payments…</Typography> : null}
    {receipts.isError ? <Alert severity="warning">Recorded payments are unavailable.
      <Button size="small" disabled={busy} onClick={() => { void recheckAccess(); }}>Reload recorded payments</Button>
    </Alert> : null}
    {receipts.isSuccess ? <ModuleReceiptHistory page={receipts.data} selectedPurchaseId={selection.purchase_id}
      selectedReceiptId={original?.receipt_id ?? null} readDisabled={busy} onView={value => {
        if (!current() || deniedRef.current || busy || !receipts.isSuccess
          || !receipts.data.items.some(item => sameReceiptOriginal(receiptOriginal(item), value))) return;
        setDetail({ selectionId, original: value });
      }} onPage={next => {
        if (!current() || deniedRef.current || busy) return;
        setDetail(null); setPaging({ selectionId, offset: next });
      }} /> : null}
    {original !== null && receipt.isPending ? <Typography role="status">Loading receipt…</Typography> : null}
    {original !== null && receipt.isError ? <Alert severity="warning">The selected receipt is unavailable.
      <Button size="small" disabled={busy} onClick={() => {
        if (current() && !deniedRef.current && !busy) void receipt.refetch();
      }}>Reload receipt</Button>
    </Alert> : null}
    {original !== null && receipt.isSuccess && sameReceiptOriginal(receiptOriginal(receipt.data), original)
      ? <ModuleReceiptDetail receipt={receipt.data} onClose={() => { if (current()) setDetail(null); }} /> : null}
  </Stack>;
}
